import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import SkipEyeLogo from './SkipEyeLogo';
import SkipEyeLogoTargeted from './SkipEyeLogoTargeted';
import PainPointSequence from './PainPointSequence';
import SignupForm from './SignupForm';
import { useResponsive } from '../hooks/useResponsive';

const LandingPage = () => {
  const navigate = useNavigate();
  const viewport = useResponsive();

  // Login state management
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  // New Customer flow state management
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [showPainPoints, setShowPainPoints] = useState(false);
  const [showSolution, setShowSolution] = useState(false);
  const [targetPosition, setTargetPosition] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showTakeControlButton, setShowTakeControlButton] = useState(false);

  // Ref for smooth scrolling to form
  const formRef = useRef(null);

  const handleLoginClick = () => {
    setShowLoginForm(true);
  };

  const handleNewCustomerClick = () => {
    setShowNewCustomer(true);
    setShowPainPoints(true);
  };

  const handlePainPointsComplete = () => {
    // Do nothing - wait for user to click "Take Control" button
  };

  const handleBackToLanding = () => {
    setShowNewCustomer(false);
    setShowPainPoints(false);
    setShowSolution(false);
    setShowForm(false);
    setShowTakeControlButton(false);
    setTargetPosition(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePositionChange = (position) => {
    setTargetPosition(position);
  };

  const handlePainPointIndexChange = (index) => {
    // Show "Take Control" button when "Ready?" message appears (index 16)
    if (index === 16) {
      setTimeout(() => {
        setShowTakeControlButton(true);
      }, 600);
    }
  };

  const handleTakeControlClick = () => {
    setShowPainPoints(false);
    setShowTakeControlButton(false);
    setTimeout(() => {
      setShowSolution(true);
      setTimeout(() => {
        setShowForm(true);
        setTimeout(() => {
          if (formRef.current) {
            formRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }, 300);
      }, 500);
    }, 500);
  };

  // Helper function to check admin status - returns userData object or null
  const checkAdminStatus = async (uid) => {
    try {
      const userDocRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        return null;
      }

      const userData = userDoc.data();
      if (userData.admin !== true) {
        return null;
      }

      return userData;
    } catch (error) {
      console.error("Error checking admin status:", error.code);
      return null;
    }
  };

  // Authentication logic with admin check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userData = await checkAdminStatus(user.uid);

          if (!userData) {
            setError('Sorry, only admin accounts are able to access the web data');
            await signOut(auth);
            setLoading(false);
            return;
          }

          // Check company suspension status (super admins skip this check)
          if (userData.superAdmin !== true && userData.companyId) {
            const companyDocRef = doc(db, 'companies', userData.companyId);
            const companyDoc = await getDoc(companyDocRef);
            if (companyDoc.exists() && companyDoc.data().status === 'suspended') {
              setError('Your account is suspended. Please contact info@skipeye.com');
              await signOut(auth);
              setLoading(false);
              return;
            }
          }

          const idTokenResult = await user.getIdTokenResult(true);
          if (idTokenResult.claims.companyId) {
            localStorage.setItem('companyId', idTokenResult.claims.companyId);
          }
          navigate('/map');
        } catch (err) {
          console.error("Error checking admin status:", err.code);
          setError('Error verifying account permissions');
          await signOut(auth);
          setLoading(false);
        }
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Convert username to synthetic email
      // If username contains '@', assume it's an admin using real email
      // Otherwise, convert to synthetic email (e.g., "DarrenSWS" -> "darrensws@skipeye.com")
      const email = username.includes('@')
        ? username.toLowerCase().trim()
        : `${username.toLowerCase().trim()}@skipeye.com`;

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await userCredential.user.getIdTokenResult(true);
    } catch (err) {
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/invalid-email' || err.code === 'auth/user-not-found') {
        setError('Invalid username or password');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed login attempts. Please try again later');
      } else {
        setError(err.message || 'Failed to login');
      }
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setResetSuccess(false);
    setLoading(true);

    try {
      const email = resetEmail.toLowerCase().trim();

      // Validate email format
      if (!email.includes('@')) {
        setError('Please enter a valid email address. Password reset is only available for admin accounts.');
        setLoading(false);
        return;
      }

      // Send password reset email
      await sendPasswordResetEmail(auth, email);
      setResetSuccess(true);
      setResetEmail('');

      // Reset form after 3 seconds
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetSuccess(false);
      }, 3000);
    } catch (err) {
      console.error("Password reset error:", err);
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email address');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many requests. Please try again later');
      } else {
        setError(err.message || 'Failed to send password reset email');
      }
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  // Animation state for staggered entrance
  const [animationReady, setAnimationReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimationReady(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Calculate logo scale based on device type (matching original implementation)
  const getLogoScale = () => {
    if (viewport.isSmallMobile) return 0.4;
    if (viewport.isMobile) return 0.5;
    if (viewport.isTablet) return 0.65;
    return 0.7;
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100dvh',
      backgroundColor: '#FAFAF9',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      position: 'relative',
      overflow: 'hidden'
    },
    // Desktop: fixed title at top, Mobile: responsive title
    titleSection: {
      position: viewport.isMobile ? 'relative' : 'absolute',
      top: viewport.isMobile ? 0 : '8vh',
      left: '50%',
      transform: 'translateX(-50%)',
      textAlign: 'center',
      zIndex: 1100,
      padding: viewport.isMobile ? 'var(--space-md) 0' : '0',
      width: '100%',
      opacity: animationReady ? 1 : 0,
      transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
      transitionDelay: '0s'
    },
    title: {
      fontSize: viewport.isMobile ? 'var(--text-2xl)' : 'var(--text-3xl)',
      fontWeight: '600',
      color: '#166534',
      margin: '0 0 var(--space-xs) 0',
      textAlign: 'center'
    },
    subtitle: {
      fontSize: viewport.isMobile ? 'var(--text-sm)' : 'var(--text-lg)',
      color: '#78716C',
      fontStyle: 'italic',
      margin: 0,
      textAlign: 'center'
    },
    // Main content area - centered vertically on desktop, stacked on mobile
    mainContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      padding: viewport.isMobile ? 'var(--space-md)' : 'var(--space-lg)',
      paddingTop: viewport.isMobile ? 0 : 'var(--space-2xl)'
    },
    logoContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
      width: '100%'
    },
    // Login button positioned above logo
    loginButtonAboveLogo: {
      position: 'absolute',
      bottom: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      marginBottom: viewport.isMobile ? 'clamp(24px, 5vw, 40px)' : 'clamp(40px, 5vh, 60px)',
      opacity: (showLoginForm || showNewCustomer) ? 0 : animationReady ? 1 : 0,
      transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
      transitionDelay: '0.2s',
      pointerEvents: (showLoginForm || showNewCustomer) ? 'none' : 'auto'
    },
    // New Customer button positioned below logo
    newCustomerButtonBelowLogo: {
      position: 'absolute',
      top: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      marginTop: viewport.isMobile ? 'clamp(24px, 5vw, 40px)' : 'clamp(40px, 5vh, 60px)',
      opacity: (showLoginForm || showNewCustomer) ? 0 : animationReady ? 1 : 0,
      transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
      transitionDelay: '0.4s',
      pointerEvents: (showLoginForm || showNewCustomer) ? 'none' : 'auto'
    },
    loginFormContainer: {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: showLoginForm ? 'translate(-50%, -50%)' : 'translate(-50%, -50%) scale(0.95)',
      zIndex: 1200,
      width: '100%',
      maxWidth: viewport.isMobile ? '320px' : '380px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 'var(--space-md)',
      opacity: showLoginForm ? 1 : 0,
      maxHeight: showLoginForm ? '600px' : '0',
      overflow: 'visible',
      transition: 'opacity 0.3s ease, max-height 0.3s ease, transform 0.3s ease',
      pointerEvents: showLoginForm ? 'auto' : 'none',
      ...(showLoginForm && {
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(28, 25, 23, 0.12)',
        border: '1px solid #E7E5E4'
      })
    },
    formField: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 'var(--space-xs)',
      width: '100%'
    },
    input: {
      width: '100%',
      padding: '12px 14px',
      backgroundColor: '#FAFAF9',
      border: '1px solid #D6D3D1',
      borderRadius: '8px',
      fontSize: '15px',
      transition: 'all 0.2s ease',
      textAlign: 'center',
      boxSizing: 'border-box',
      minHeight: '44px',
      color: '#292524',
      outline: 'none'
    },
    button: {
      padding: viewport.isMobile ? '0.75rem 1.75rem' : '0.875rem 2rem',
      fontSize: viewport.isMobile ? 'var(--text-base)' : 'var(--text-lg)',
      fontWeight: '500',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(22, 101, 52, 0.15)',
      minWidth: viewport.isMobile ? '180px' : '200px',
      minHeight: '44px',
      letterSpacing: '0.3px',
      backgroundColor: '#166534',
      color: 'white',
      width: viewport.isMobile ? '100%' : 'auto',
      maxWidth: viewport.isMobile ? '280px' : 'none'
    },
    signInButton: {
      width: '100%',
      padding: 'var(--space-sm)',
      backgroundColor: '#166534',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: 'var(--text-base)',
      fontWeight: '500',
      cursor: loading ? 'not-allowed' : 'pointer',
      opacity: loading ? 0.7 : 1,
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(22, 101, 52, 0.15)',
      boxSizing: 'border-box',
      letterSpacing: '0.3px',
      minHeight: '44px'
    },
    errorMessage: {
      backgroundColor: 'rgba(254, 226, 226, 0.9)',
      color: '#B91C1C',
      padding: 'var(--space-sm)',
      borderRadius: 'var(--radius-md)',
      fontSize: 'var(--text-sm)',
      textAlign: 'center',
      width: '100%'
    },
    privacySection: {
      position: viewport.isMobile ? 'relative' : 'absolute',
      bottom: viewport.isMobile ? 0 : 'clamp(20px, 3vh, 30px)',
      left: '50%',
      transform: 'translateX(-50%)',
      textAlign: 'center',
      zIndex: 10,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      padding: viewport.isMobile ? 'var(--space-sm) var(--space-md)' : '8px 16px',
      borderRadius: '6px',
      boxShadow: '0 1px 3px rgba(28, 25, 23, 0.08)',
      border: '1px solid #E7E5E4',
      width: viewport.isMobile ? '100%' : 'auto',
      opacity: showNewCustomer ? 0 : animationReady ? 1 : 0,
      transition: 'opacity 0.6s ease-out',
      transitionDelay: '0.5s'
    },
    privacyLink: {
      color: '#166534',
      textDecoration: 'none',
      fontSize: '13px',
      fontWeight: '500'
    },
    solutionContent: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#f0f0f0',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: showForm ? 'flex-start' : 'center',
      zIndex: 1001,
      padding: viewport.isMobile ? 'var(--space-lg) var(--space-md)' : 'var(--space-2xl) var(--space-lg)',
      paddingBottom: 'var(--space-2xl)',
      overflow: 'auto'
    },
    backButton: {
      position: 'absolute',
      top: viewport.isMobile ? 'var(--space-md)' : 'var(--space-xl)',
      left: viewport.isMobile ? 'var(--space-md)' : 'var(--space-xl)',
      padding: viewport.isMobile ? '0.625rem 1.25rem' : '0.75rem 1.5rem',
      fontSize: '14px',
      fontWeight: '500',
      backgroundColor: '#78716C',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(28, 25, 23, 0.1)',
      zIndex: 1002,
      minHeight: '44px'
    },
    takeControlButton: {
      position: 'fixed',
      top: viewport.isMobile ? '70%' : '65%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      padding: viewport.isMobile ? '0.875rem 1.75rem' : '1rem 2.5rem',
      fontSize: viewport.isMobile ? 'var(--text-lg)' : 'var(--text-xl)',
      fontWeight: '500',
      backgroundColor: '#166534',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 4px 16px rgba(22, 101, 52, 0.3)',
      zIndex: 1100,
      opacity: showTakeControlButton ? 1 : 0,
      pointerEvents: showTakeControlButton ? 'auto' : 'none',
      letterSpacing: '0.3px',
      minWidth: viewport.isMobile ? '200px' : '240px',
      minHeight: '44px',
      animation: showTakeControlButton ? 'pulse 2s ease-in-out infinite' : 'none',
      width: viewport.isMobile ? 'calc(100% - 2rem)' : 'auto',
      maxWidth: viewport.isMobile ? '320px' : 'none'
    },
    loginBackdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1150,
      opacity: showLoginForm ? 1 : 0,
      pointerEvents: showLoginForm ? 'auto' : 'none',
      transition: 'opacity 0.5s ease'
    },
    closeButton: {
      position: 'absolute',
      top: 'var(--space-sm)',
      right: 'var(--space-sm)',
      backgroundColor: 'transparent',
      border: 'none',
      fontSize: 'var(--text-xl)',
      color: 'var(--color-gray-600)',
      cursor: 'pointer',
      padding: 'var(--space-xs)',
      lineHeight: 1,
      minWidth: '44px',
      minHeight: '44px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 'var(--radius-md)',
      transition: 'all 0.2s ease'
    },
    loginTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#166534',
      textAlign: 'center',
      marginBottom: 'var(--space-md)',
      marginTop: 'var(--space-sm)'
    },
    forgotPasswordLink: {
      fontSize: '13px',
      color: '#166534',
      textDecoration: 'none',
      cursor: 'pointer',
      textAlign: 'center',
      marginTop: 'var(--space-xs)',
      transition: 'color 0.2s ease'
    },
    successMessage: {
      backgroundColor: 'rgba(220, 252, 231, 0.9)',
      color: '#065F46',
      padding: 'var(--space-sm)',
      borderRadius: 'var(--radius-md)',
      fontSize: 'var(--text-sm)',
      textAlign: 'center',
      width: '100%'
    },
    backToLoginLink: {
      fontSize: '13px',
      color: '#166534',
      textDecoration: 'none',
      cursor: 'pointer',
      textAlign: 'center',
      marginTop: 'var(--space-md)',
      transition: 'color 0.2s ease'
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              box-shadow: 0 4px 16px rgba(22, 101, 52, 0.3);
            }
            50% {
              box-shadow: 0 6px 24px rgba(22, 101, 52, 0.45);
            }
          }
        `}
      </style>
      <div style={styles.container}>
        {/* Title Section - Hidden when showing form */}
        {!showSolution && (
          <div style={styles.titleSection}>
            <h1 style={styles.title}>SkipEye</h1>
            <p style={styles.subtitle}>Your Eyes in the Sky</p>
          </div>
        )}

        {/* Main Content Area */}
        <div style={styles.mainContent}>
          {/* Logo Container - serves as reference point for buttons */}
          <div style={styles.logoContainer}>
            {/* Login Backdrop - Shown when login form is visible */}
            <div
              style={styles.loginBackdrop}
              onClick={() => setShowLoginForm(false)}
            />

            {/* Login Form - Modal centered on screen */}
            <div style={styles.loginFormContainer}>
              {/* Close button */}
              {showLoginForm && (
                <button
                  style={styles.closeButton}
                  onClick={() => setShowLoginForm(false)}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--color-gray-200)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                  aria-label="Close login form"
                >
                  ✕
                </button>
              )}

              {/* Title for login modal */}
              {showLoginForm && (
                <h2 style={styles.loginTitle}>
                  {showForgotPassword ? 'Reset Password' : 'Login'}
                </h2>
              )}

              {error && (
                <div style={styles.errorMessage}>
                  {error}
                </div>
              )}

              {resetSuccess && (
                <div style={styles.successMessage}>
                  Password reset email sent! Check your inbox.
                </div>
              )}

              {!showForgotPassword ? (
                <>
                  <div style={styles.formField}>
                    <input
                      id="username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value.trim())}
                      required
                      style={styles.input}
                      placeholder="Username"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleLogin(e);
                        }
                      }}
                    />
                  </div>

                  <div style={styles.formField}>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      style={styles.input}
                      placeholder="Password"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleLogin(e);
                        }
                      }}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleLogin}
                    disabled={loading}
                    style={styles.signInButton}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.target.style.backgroundColor = '#14532D';
                        e.target.style.boxShadow = '0 4px 12px rgba(22, 101, 52, 0.25)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading) {
                        e.target.style.backgroundColor = '#166534';
                        e.target.style.boxShadow = '0 2px 8px rgba(22, 101, 52, 0.15)';
                      }
                    }}
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </button>

                  <div
                    style={styles.forgotPasswordLink}
                    onClick={() => {
                      setShowForgotPassword(true);
                      setError(null);
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = '#14532D';
                      e.target.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = '#166534';
                      e.target.style.textDecoration = 'none';
                    }}
                  >
                    Forgot password?
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '13px', color: '#78716C', textAlign: 'center', marginBottom: 'var(--space-sm)' }}>
                    Enter your admin email address and we'll send you a password reset link.
                  </div>

                  <div style={styles.formField}>
                    <input
                      id="resetEmail"
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value.trim())}
                      required
                      style={styles.input}
                      placeholder="Admin Email Address"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleForgotPassword(e);
                        }
                      }}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={loading}
                    style={styles.signInButton}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.target.style.backgroundColor = '#14532D';
                        e.target.style.boxShadow = '0 4px 12px rgba(22, 101, 52, 0.25)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading) {
                        e.target.style.backgroundColor = '#166534';
                        e.target.style.boxShadow = '0 2px 8px rgba(22, 101, 52, 0.15)';
                      }
                    }}
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>

                  <div
                    style={styles.backToLoginLink}
                    onClick={() => {
                      setShowForgotPassword(false);
                      setError(null);
                      setResetEmail('');
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = '#14532D';
                      e.target.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = '#166534';
                      e.target.style.textDecoration = 'none';
                    }}
                  >
                    ← Back to login
                  </div>
                </>
              )}
            </div>

            {/* Login Button - Positioned above logo */}
            <div style={styles.loginButtonAboveLogo}>
              <button
                style={styles.button}
                onClick={handleLoginClick}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#14532D';
                  e.target.style.boxShadow = '0 4px 12px rgba(22, 101, 52, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#166534';
                  e.target.style.boxShadow = '0 2px 8px rgba(22, 101, 52, 0.15)';
                }}
              >
                Login
              </button>
            </div>

            {/* Logo - The center reference point */}
            <div style={{
              transform: `scale(${getLogoScale()})`,
              transformOrigin: 'center center',
              opacity: animationReady ? 1 : 0,
              transition: 'opacity 0.6s ease-out',
              transitionDelay: '0.1s'
            }}>
              {showNewCustomer ? (
                <SkipEyeLogoTargeted size="large" targetPosition={targetPosition} />
              ) : (
                <SkipEyeLogo size="large" />
              )}
            </div>

            {/* New Customer Button - Positioned below logo */}
            <div style={styles.newCustomerButtonBelowLogo}>
              <button
                style={styles.button}
                onClick={handleNewCustomerClick}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#14532D';
                  e.target.style.boxShadow = '0 4px 12px rgba(22, 101, 52, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#166534';
                  e.target.style.boxShadow = '0 2px 8px rgba(22, 101, 52, 0.15)';
                }}
              >
                New Customer
              </button>
            </div>
          </div>
        </div>

        {/* Pain Points Sequence */}
        {showPainPoints && (
          <>
            <PainPointSequence
              onComplete={handlePainPointsComplete}
              onPositionChange={handlePositionChange}
              onIndexChange={handlePainPointIndexChange}
            />

            {/* Take Control Button */}
            <button
              style={styles.takeControlButton}
              onClick={handleTakeControlClick}
              onMouseEnter={(e) => {
                if (showTakeControlButton) {
                  e.target.style.backgroundColor = '#14532D';
                  e.target.style.boxShadow = '0 6px 24px rgba(22, 101, 52, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (showTakeControlButton) {
                  e.target.style.backgroundColor = '#166534';
                  e.target.style.boxShadow = '0 4px 16px rgba(22, 101, 52, 0.3)';
                }
              }}
            >
              Take Control →
            </button>
          </>
        )}

        {/* Form Content */}
        {showSolution && (
          <div style={styles.solutionContent}>
            <button
              style={styles.backButton}
              onClick={handleBackToLanding}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#57534E';
                e.target.style.boxShadow = '0 4px 12px rgba(28, 25, 23, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#78716C';
                e.target.style.boxShadow = '0 2px 8px rgba(28, 25, 23, 0.1)';
              }}
            >
              ← Back
            </button>

            {showForm && (
              <div ref={formRef} style={{
                opacity: showForm ? 1 : 0,
                transform: showForm ? 'translateY(0)' : 'translateY(20px)',
                transition: 'all 0.8s ease-in-out',
                marginTop: viewport.isMobile ? 'var(--space-2xl)' : '120px',
                width: '100%',
                paddingTop: 'var(--space-lg)'
              }}>
                <SignupForm onSubmit={() => {}} />
              </div>
            )}
          </div>
        )}

        {/* Privacy Policy Section */}
        <div style={styles.privacySection}>
          <a
            href="/privacy-policy.html"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.privacyLink}
          >
            Privacy Policy
          </a>
        </div>
      </div>
    </>
  );
};

export default LandingPage;
